<script setup lang="ts">
import { computed, type CSSProperties } from "vue"
import SVG_Letterform from "@/components/SVG_Letterform.vue"
import { letterforms } from "@/data/letterforms"
import type { JackpotState } from "@/composables/use-jackpot"

const props = defineProps<{
  letter: "c" | "m"
  image: string
  srcset?: string
  state: JackpotState
  chargeLevel: number
  pressedLetter: "c" | "m" | null
  hoveredLetter?: "c" | "m" | null
  grow: number
}>()

const emit = defineEmits<{
  "press-start": []
  "press-end": []
  "hover-start": []
  "hover-end": []
}>()

type RowVisuals = {
  fillClass: string
  stroke: string
  strokeWidth: number
  imageOpacity: number
  imageTransition: string
  imagePosition: string
  useSrcset: boolean
}

function makeVisuals(letter: "c" | "m", state: JackpotState): RowVisuals {
  // const fillClass = letter === "c" ? "fill-gold" : "fill-blue"
  const base: RowVisuals = {
    fillClass: "fill-transparent",
    stroke: "black",
    strokeWidth: 1,
    imageOpacity: 1,
    imageTransition: "opacity 0.5s ease",
    imagePosition: "center center",
    useSrcset: true,
  }
  if (state === "paused") {
    return {
      ...base,
      fillClass: "fill-transparent",
      imageOpacity: 0,
      imageTransition: "opacity 0.5s ease",
    }
  }
  if (state === "intro")
    return {
      ...base,
      fillClass: "fill-black",
    }
  if (state === "spinning")
    return {
      ...base,
      imageOpacity: 1,
      imageTransition: "none",
      useSrcset: false,
    }
  if (state === "result")
    return {
      ...base,
      imageOpacity: 1,
      imagePosition: letter === "c" ? "center top" : "center bottom",
    }
  return base
}

const isResultHover = computed(
  () => props.state === "result" && props.hoveredLetter === props.letter,
)

const visuals = computed(() => {
  const v = makeVisuals(props.letter, props.state)
  if (isResultHover.value) v.imageOpacity = 0.55
  return v
})

const isPressed = computed(() => props.pressedLetter === props.letter)

// Spring owns all layout — no CSS transition ever.
const rowStyle = computed<CSSProperties>(() => ({
  flexGrow: props.grow,
  flexBasis: "0%",
}))

// Strain shake: amplitude ramps with charge²
const shake = computed(() => {
  if (props.state !== "charging" || !isPressed.value || props.chargeLevel <= 0)
    return "none"
  const amp = props.chargeLevel * props.chargeLevel * 3
  const dx = (Math.random() - 0.5) * amp
  const dy = (Math.random() - 0.5) * amp
  return `translate(${dx}px, ${dy}px)`
})
</script>

<template>
  <div
    class="min-h-0 overflow-hidden cursor-pointer relative select-none touch-none [-webkit-touch-callout:none] [-webkit-user-select:none]"
    :style="{ ...rowStyle, transform: shake }"
    @pointerdown="
      e => {
        ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
        emit('press-start')
      }
    "
    @pointerup="emit('press-end')"
    @pointercancel="emit('press-end')"
    @pointerenter="
      e => {
        if ((e as PointerEvent).pointerType === 'mouse') emit('hover-start')
      }
    "
    @pointerleave="
      e => {
        if ((e as PointerEvent).pointerType === 'mouse') emit('hover-end')
      }
    "
  >
    <SVG_Letterform
      :masters="letterforms[letter]"
      :min-gap-scale="{ default: 4, lg: 8 }"
      :stroke-width="visuals.strokeWidth"
      :stroke="visuals.stroke"
      class="fill-transparent"
      interactive
      global
      v-slot="{ width, height, clipId }"
    >
      <g :clip-path="`url(#${clipId})`" class="pointer-events-none">
        <rect :width="width" :height="height" :class="visuals.fillClass" />
        <foreignObject x="0" y="0" :width="width" :height="height">
          <img
            v-if="image"
            draggable="false"
            :srcset="visuals.useSrcset ? srcset : undefined"
            :src="image"
            :sizes="visuals.useSrcset ? `${width}px` : undefined"
            :style="{
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              opacity: visuals.imageOpacity,
              transition: visuals.imageTransition,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: visuals.imagePosition,
              display: 'block',
            }"
          />
        </foreignObject>
      </g>
    </SVG_Letterform>
  </div>
</template>
